import { useQueryClient } from '@tanstack/react-query';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import { useDeleteSchedulesById, getGetSchedulesQueryKey } from '@/api/control/control-plane/control-plane';
import type { ScheduleDto } from '@/api/control/missionImpactAPI.schemas';

interface DeleteScheduleDialogProps {
  orgId: string;
  profileId: string;
  schedule: ScheduleDto | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Dialog component for confirming schedule deletion
 */
export function DeleteScheduleDialog({
  orgId,
  profileId,
  schedule,
  open,
  onOpenChange,
}: DeleteScheduleDialogProps) {
  const queryClient = useQueryClient();
  const { t } = useLanguage();

  const deleteScheduleMutation = useDeleteSchedulesById({
    mutation: {
      onSuccess: () => {
        toast.success(t.dialogs.deleteSchedule.successMessage);
        void queryClient.invalidateQueries({ queryKey: getGetSchedulesQueryKey(orgId, profileId) });
        onOpenChange(false);
      },
      onError: () => {
        toast.error(t.dialogs.deleteSchedule.errorMessage);
      },
    },
  });

  const handleConfirm = () => {
    if (schedule?.id) {
      deleteScheduleMutation.mutate({
        orgId,
        profileId,
        scheduleId: schedule.id,
      });
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t.dialogs.deleteSchedule.title}</AlertDialogTitle>
          <AlertDialogDescription>
            {t.dialogs.deleteSchedule.description}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{t.dialogs.deleteSchedule.cancel}</AlertDialogCancel>
          <AlertDialogAction onClick={handleConfirm}>{t.dialogs.deleteSchedule.delete}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
